import type {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    DetailedHTMLProps,
    FC,
    MouseEvent
} from "react";

import { css, cssMap } from "@compiled/react";

import { DataEventProps } from "@/libraries/wpl/foundations/src";

type ButtonSize = "large" | "medium" | "small";
export type ButtonColor =
    | "blue"
    | "yellow"
    | "white"
    | "light_grey"
    | "black"
    | "disabled";
type ButtonVariant = "primary" | "secondary";

const getButtonSize = (size: string | undefined): ButtonSize => {
    const buttonSize = size?.toLowerCase();
    if (buttonSize && buttonSize === "small") {
        return "small";
    }
    if (buttonSize && buttonSize === "large") {
        return "large";
    }
    return "medium";
};

const getButtonVariant = (type: string | undefined): ButtonVariant => {
    const buttonType = type?.toLowerCase();
    if (buttonType === "secondary") {
        return "secondary";
    }
    return "primary";
};

const isButtonColor = (color: string): color is ButtonColor => {
    return (
        color === "blue" ||
        color === "yellow" ||
        color === "white" ||
        color === "black" ||
        color === "light_grey" ||
        color === "disabled"
    );
};

const getButtonColor = (
    color: string | undefined,
    disabled: boolean | undefined
): ButtonColor => {
    if (disabled) {
        return "disabled";
    }

    const buttonColor = color?.toLowerCase();

    if (buttonColor && isButtonColor(buttonColor)) {
        return buttonColor;
    }

    return "blue";
};

const buttonPrimaryStyles = cssMap({
    primary_blue: {
        color: "#ffffff",
        backgroundColor: "#1868db",
        border: "none"
    },
    primary_yellow: {
        color: "#101214",
        backgroundColor: "#ffab00",
        border: "none"
    },
    primary_white: {
        color: "#101214",
        backgroundColor: "#ffffff",
        border: "none"
    },
    primary_black: {
        color: "#ffffff",
        backgroundColor: "#101214",
        border: "none"
    },
    primary_light_grey: {
        color: "#101214",
        backgroundColor: "#F8F8F8",
        border: "none"
    },
    primary_disabled: {
        color: "#97a0af",
        backgroundColor: "#ebecf0",
        border: "none",
        cursor: "not-allowed"
    },
    secondary_blue: {
        color: "#0052cc",
        backgroundColor: "transparent",
        border: "2px solid #1868db"
    },
    secondary_yellow: {
        color: "#ffa900",
        backgroundColor: "transparent",
        border: "2px solid #ffa900"
    },
    secondary_white: {
        color: "#ffffff",
        backgroundColor: "transparent",
        border: "2px solid #ffffff"
    },
    secondary_black: {
        color: "#101214",
        backgroundColor: "transparent",
        border: "2px solid #101214"
    },
    secondary_light_grey: {
        color: "#101214",
        backgroundColor: "#F8F8F8",
        border: "2px solid #101214"
    },
    secondary_disabled: {
        color: "#97a0af",
        backgroundColor: "#ebecf0",
        border: "none",
        cursor: "not-allowed"
    }
});

const baseStyles = css({
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    display: "inline-flex",
    fontWeight: 500,
    fontFamily: "var(--bxp-charlie-text-font-family)",
    lineHeight: "1.25rem",
    padding: "0 1rem",
    textAlign: "center",
    textDecoration: "none"
});

const fontAndHeightStyles = cssMap({
    large: {
        fontSize: "1rem",
        borderRadius: 24,
        padding: "12px 24px",
        height: "min-content",
        minHeight: "48px"
    },
    medium: {
        fontSize: "1rem",
        borderRadius: 20,
        padding: "8px 20px",
        height: "min-content",
        minHeight: "40px"
    },
    small: {
        fontSize: "0.875rem",
        borderRadius: 16,
        padding: "4px 16px",
        height: "min-content",
        minHeight: "32px"
    }
});

export type Adg4ButtonProps = {
    size?: "large" | "medium" | "small";
    variant?: "primary" | "secondary";
    color?: ButtonColor;
    disabled?: boolean;
    className?: string;
    dataEventProps?: DataEventProps;
} & (
    | DetailedHTMLProps<
          ButtonHTMLAttributes<HTMLButtonElement>,
          HTMLButtonElement
      >
    | DetailedHTMLProps<
          AnchorHTMLAttributes<HTMLAnchorElement>,
          HTMLAnchorElement
      >
);

function isLinkArgs(
    props: unknown
): props is AnchorHTMLAttributes<HTMLAnchorElement> {
    return "href" in (props as AnchorHTMLAttributes<HTMLAnchorElement>);
}

export const Adg4Button: FC<Adg4ButtonProps> = ({
    variant,
    color,
    size,
    disabled,
    className,
    children,
    dataEventProps,
    ...props
}: Adg4ButtonProps) => {
    const isLink = isLinkArgs(props);
    const buttonVariant = getButtonVariant(variant);
    const buttonColor = getButtonColor(color, disabled);
    const buttonSize: ButtonSize = getButtonSize(size);

    const variantStyles =
        buttonPrimaryStyles[`${buttonVariant}_${buttonColor}`];
    const fontAndHeightStyle = fontAndHeightStyles[buttonSize];

    const handleOnClick = (
        e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>
    ) => {
        if (disabled) {
            e.preventDefault();
        } else if (props.onClick) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            props.onClick(e as any);
        }
    };

    return isLink ? (
        <a
            className={className}
            css={[baseStyles, variantStyles, fontAndHeightStyle]}
            {...props}
            {...dataEventProps}
            onClick={handleOnClick}
            href={props.href}
        >
            {children}
        </a>
    ) : (
        <button
            className={className}
            css={[baseStyles, variantStyles, fontAndHeightStyle]}
            {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
            onClick={handleOnClick}
        >
            {children}
        </button>
    );
};
